/**
 * Time formatting utilities with locale-aware display.
 * Single source of truth for time formatting across the application.
 *
 * @module utils/timeFormat
 */

/**
 * Fixed 24-hour time format (e.g., "15:30").
 * @type {Intl.DateTimeFormatOptions}
 */
export const TIME_24H_FORMAT = {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
};

/**
 * Fixed 12-hour time format with AM/PM (e.g., "3:30 PM").
 * @type {Intl.DateTimeFormatOptions}
 */
export const TIME_12H_FORMAT = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
};

/**
 * A 24-hour locale writes a numeric hour as "0:05" and "9:05"; its convention is "00:05" and "09:05".
 * @returns {'numeric'|'2-digit'}
 */
function localeHourDigits() {
    const {hourCycle} = new Intl.DateTimeFormat(undefined, {hour: 'numeric'}).resolvedOptions();

    return hourCycle === 'h23' || hourCycle === 'h24' ? '2-digit' : 'numeric';
}

/**
 * Auto-detect time format based on user's locale.
 * Browser automatically chooses 12h or 24h format based on locale preference.
 * e.g., US users see "3:30 PM", German users see "15:30"
 * @type {Intl.DateTimeFormatOptions}
 */
export const TIME_AUTO_FORMAT = {
    hour: localeHourDigits(),
    minute: '2-digit',
    hourCycle: undefined, // Let browser auto-detect based on locale
};

/**
 * Date format (e.g., "Dec 16, 2025").
 * @type {Intl.DateTimeFormatOptions}
 */
export const DATE_FORMAT = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
};

/**
 * Date format with the weekday (e.g., "Tue, Dec 16, 2025").
 * @type {Intl.DateTimeFormatOptions}
 */
export const DATE_WEEKDAY_FORMAT = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
};

/**
 * Short month name (e.g., "Dec").
 * @type {Intl.DateTimeFormatOptions}
 */
export const MONTH_FORMAT = {month: 'short'};

/**
 * Day of the month (e.g., "16").
 * @type {Intl.DateTimeFormatOptions}
 */
export const DAY_FORMAT = {day: 'numeric'};

/**
 * Fixed 24-hour datetime format (e.g., "Dec 16, 2025, 15:30").
 * @type {Intl.DateTimeFormatOptions}
 */
export const DATETIME_24H_FORMAT = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
};

/**
 * Auto-detect datetime format based on user's locale.
 * Uses explicit format with "at" separator: "Jan 29, 2026 at 3:30 PM" or "Jan 29, 2026 at 15:30"
 * @type {Intl.DateTimeFormatOptions}
 */
export const DATETIME_AUTO_FORMAT = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: localeHourDigits(),
    minute: '2-digit',
};

/**
 * Month, day and time with auto-detect hour format, for the end of a range on another day (e.g., "Dec 17, 3:30 PM").
 * @type {Intl.DateTimeFormatOptions}
 */
export const MONTH_DAY_TIME_AUTO_FORMAT = {
    month: 'short',
    day: 'numeric',
    hour: localeHourDigits(),
    minute: '2-digit',
};

/**
 * Parse a time string (HH:mm) into total minutes from midnight.
 * @param {string} timeString - Time in HH:mm format (e.g., "14:30")
 * @returns {number} Minutes from midnight (e.g., 870 for "14:30")
 * @example
 * parseTimeToMinutes('00:00') // 0
 * parseTimeToMinutes('14:30') // 870
 * parseTimeToMinutes('23:59') // 1439
 */
export function parseTimeToMinutes(timeString) {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
}

/**
 * Convert minutes from midnight to HH:mm format.
 * @param {number} totalMinutes - Minutes from midnight (0-1439)
 * @returns {string} Time in HH:mm format (e.g., "14:30")
 * @example
 * minutesToTimeString(0) // "00:00"
 * minutesToTimeString(870) // "14:30"
 * minutesToTimeString(1439) // "23:59"
 */
export function minutesToTimeString(totalMinutes) {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

/**
 * Format a time string (HH:mm) to locale-aware display format.
 * Auto-detects 12h/24h based on user's browser locale.
 * @param {string} timeString - Time in HH:mm format (e.g., "14:30")
 * @param {string} [locale] - Optional locale override for testing (e.g., "en-US", "pt-BR")
 * @returns {string} Locale-formatted time (e.g., "2:30 PM" or "14:30")
 * @example
 * formatTimeAuto('14:30') // "2:30 PM" (US) or "14:30" (Brazil)
 * formatTimeAuto('14:30', 'en-US') // "2:30 PM"
 * formatTimeAuto('14:30', 'pt-BR') // "14:30"
 */
export function formatTimeAuto(timeString, locale) {
    const [hours, minutes] = timeString.split(':').map(Number);
    const date = new Date(2000, 0, 1, hours, minutes);
    return date.toLocaleString(locale, TIME_AUTO_FORMAT);
}
